// Version: 1.4
// v1.4: The light page. The title, subtitle and the results count were drawn in
//       near-white on the grey page and could not be seen. The Hide dormant tick box
//       sits beside its text (index.css v1.6).
// v1.3: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size. Panels still drawn dark carry className="surface-dark" so their buttons pick up dark values.
// 1.2: Recent searches. Every search you run is kept (app.py v3.89), newest
//      first, in a Recent searches panel under Saved searches: click one to
//      fill the form back in, Run to fill and search, or remove it. Shows the
//      locations, date range, filters and how many results it found.
// 1.1: Search box now accepts free-text locations (towns, council areas, counties,
//      regions) as well as postcodes and districts — relabelled, with a matching
//      placeholder and help text. Backend (app.py v3.64) passes place names
//      straight to the Companies House location filter.
// 1.0: Prospect Search — find newly incorporated companies to pursue.
//      - Companies House search across several postcodes with a preset or
//        custom incorporation date range. The backend pages through every
//        result up to the chosen cap, so the old 50 result limit is gone.
//      - Company type, sector include and exclude, dormant companies hidden
//        by default.
//      - Each row shows Existing client, In pipeline (with stage) or
//        Dismissed. Tick rows to add them to the pipeline or dismiss them.
//      - Export the rows on screen to CSV or XLSX.
//      - Saved searches, optionally refreshed weekly, with a count of new
//        companies since the last run.
//      No email or domain lookups happen here; that is done per lead in the
//      Pipeline so searches stay fast and nothing is guessed in bulk.
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useIsMobile, api, downloadPost, fmtDate, fmtUpdated, STAGE_META, Badge, Modal, ErrorBox,
  pageStyle, labelStyle, inputStyle, sectionTitle,
} from "./salesShared";

const RANGE_PRESETS = [
  { days: 7, label: "7 days" }, { days: 30, label: "30 days" }, { days: 60, label: "60 days" },
  { days: 90, label: "90 days" }, { days: 180, label: "6 months" }, { days: 365, label: "1 year" },
];

function daysAgoYmd(days) {
  const d = new Date(Date.now() - days * 86400000);
  return d.toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

export default function SalesSearch() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [postcodes, setPostcodes] = useState("");
  const [rangeMode, setRangeMode] = useState(30);       // number of days or "custom"
  const [dateFrom, setDateFrom] = useState(daysAgoYmd(30));
  const [dateTo, setDateTo] = useState(daysAgoYmd(0));
  const [types, setTypes] = useState([]);
  const [includeSectors, setIncludeSectors] = useState([]);
  const [excludeSectors, setExcludeSectors] = useState([]);
  const [excludeDormant, setExcludeDormant] = useState(true);
  const [cap, setCap] = useState(1000);
  const [showSectors, setShowSectors] = useState(false);

  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [textFilter, setTextFilter] = useState("");
  const [hideKnown, setHideKnown] = useState(false);
  const [hideDismissed, setHideDismissed] = useState(true);
  const [onlyNew, setOnlyNew] = useState(false);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const [saved, setSaved] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveWeekly, setSaveWeekly] = useState(true);
  const [showSaved, setShowSaved] = useState(!isMobile);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(!isMobile);

  useEffect(() => {
    api("/api/sales/meta").then(m => { setMeta(m); setTypes(m.default_company_types || []); }).catch(e => setError(e.message));
    api("/api/sales/saved-searches").then(setSaved).catch(() => {});
    api("/api/sales/search-history").then(setHistory).catch(() => {});
  }, []);

  // Put a set of search parameters back into the form
  const applyParams = useCallback((p) => {
    p = p || {};
    setPostcodes((p.postcodes || []).join(", "));
    if (p.company_types) setTypes(p.company_types);
    setIncludeSectors(p.include_sectors || []);
    setExcludeSectors(p.exclude_sectors || []);
    setExcludeDormant(p.exclude_dormant !== false);
    setCap(p.max_per_postcode || 1000);
    if (p.date_from || p.date_to) {
      setRangeMode("custom"); setDateFrom(p.date_from || daysAgoYmd(30)); setDateTo(p.date_to || daysAgoYmd(0));
    } else if (RANGE_PRESETS.some(r => r.days === p.days)) {
      setRangeMode(p.days);
    } else {
      setRangeMode("custom"); setDateFrom(daysAgoYmd(p.days || 30)); setDateTo(daysAgoYmd(0));
    }
    if ((p.include_sectors || []).length || (p.exclude_sectors || []).length) setShowSectors(true);
  }, []);

  const buildParams = useCallback(() => {
    const p = {
      postcodes: postcodes.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean),
      company_types: types, include_sectors: includeSectors, exclude_sectors: excludeSectors,
      exclude_dormant: excludeDormant, max_per_postcode: cap,
    };
    if (rangeMode === "custom") { p.date_from = dateFrom; p.date_to = dateTo; }
    else { p.days = rangeMode; }
    return p;
  }, [postcodes, types, includeSectors, excludeSectors, excludeDormant, cap, rangeMode, dateFrom, dateTo]);

  async function runSearch(paramsOverride) {
    setError(""); setNotice(""); setSearching(true); setSelected({});
    try {
      const r = await api("/api/sales/search", { method: "POST", body: paramsOverride || buildParams() });
      setResult(r); setOnlyNew(false);
      api("/api/sales/search-history").then(setHistory).catch(() => {});
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  }

  async function historyAction(h, action) {
    if (action === "fill") { applyParams(h.params); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (action === "run") { applyParams(h.params); runSearch(h.params); return; }
    try {
      if (action === "remove") setHistory(await api(`/api/sales/search-history/${h.id}`, { method: "DELETE" }));
      if (action === "clear" && window.confirm("Clear all your recent searches?")) setHistory(await api("/api/sales/search-history", { method: "DELETE" }));
    } catch (e) { setError(e.message); }
  }

  function describeParams(p) {
    p = p || {};
    const where = (p.postcodes || []).join(", ") || "No location";
    const when = p.date_from || p.date_to ? `${fmtDate(p.date_from) || "start"} to ${fmtDate(p.date_to) || "today"}`
      : `Last ${RANGE_PRESETS.find(r => r.days === p.days)?.label || `${p.days || 30} days`}`;
    const extra = [];
    if ((p.include_sectors || []).length) extra.push(`${p.include_sectors.length} sector${p.include_sectors.length === 1 ? "" : "s"}`);
    if ((p.exclude_sectors || []).length) extra.push(`${p.exclude_sectors.length} excluded`);
    if (p.exclude_dormant === false) extra.push("dormant included");
    return { where, detail: [when, ...extra].join(" · ") };
  }

  async function runSaved(s) {
    setError(""); setNotice(""); setSearching(true); setSelected({});
    applyParams(s.params);
    try {
      const r = await api(`/api/sales/saved-searches/${s.id}/run`, { method: "POST" });
      setResult(r);
      const newCount = r.companies.filter(c => c.is_new).length;
      setOnlyNew(newCount > 0);
      if (newCount) setNotice(`${newCount} new compan${newCount === 1 ? "y" : "ies"} since the last run of "${s.name}".`);
      api("/api/sales/saved-searches").then(setSaved).catch(() => {});
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  }

  async function saveSearch() {
    if (!saveName.trim()) return;
    setBusy(true);
    try {
      const list = await api("/api/sales/saved-searches", { method: "POST", body: { name: saveName.trim(), schedule: saveWeekly ? "weekly" : "", params: buildParams() } });
      setSaved(list); setShowSave(false); setSaveName(""); setShowSaved(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function savedAction(s, action) {
    try {
      if (action === "delete") {
        if (!window.confirm(`Delete saved search "${s.name}"?`)) return;
        setSaved(await api(`/api/sales/saved-searches/${s.id}`, { method: "DELETE" }));
      } else if (action === "toggle") {
        setSaved(await api(`/api/sales/saved-searches/${s.id}`, { method: "PUT", body: { schedule: s.schedule === "weekly" ? "" : "weekly" } }));
      }
    } catch (e) { setError(e.message); }
  }

  const rows = useMemo(() => {
    if (!result) return [];
    const q = textFilter.trim().toLowerCase();
    return result.companies.filter(c => {
      if (hideDismissed && c.dismissed) return false;
      if (hideKnown && (c.client_slug || c.lead_id)) return false;
      if (onlyNew && !c.is_new) return false;
      if (q && !(`${c.company_name} ${c.company_number} ${c.address} ${(c.sectors || []).join(" ")}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [result, textFilter, hideKnown, hideDismissed, onlyNew]);

  const selectable = rows.filter(c => !c.lead_id && !c.client_slug);
  const selectedRows = rows.filter(c => selected[c.company_number]);
  const allSelected = selectable.length > 0 && selectable.every(c => selected[c.company_number]);

  function patchRows(numbers, patch) {
    setResult(r => r && ({ ...r, companies: r.companies.map(c => numbers.includes(c.company_number) ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) } : c) }));
  }

  async function addToPipeline(list) {
    if (!list.length) return;
    setBusy(true); setError("");
    try {
      const res = await api("/api/sales/leads", { method: "POST", body: { companies: list } });
      const ids = res.ids || [];
      setResult(r => r && ({ ...r, companies: r.companies.map(c => {
        const idx = list.findIndex(x => x.company_number === c.company_number);
        return idx >= 0 ? { ...c, lead_id: ids[idx] || c.lead_id || -1, lead_stage: c.lead_stage || "New" } : c;
      }) }));
      setSelected({});
      setNotice(`${res.created} added to the pipeline${res.existing ? `, ${res.existing} already there` : ""}.`);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function dismiss(list, undo = false) {
    if (!list.length) return;
    try {
      await api("/api/sales/dismiss", { method: "POST", body: { companies: list.map(c => ({ company_number: c.company_number, company_name: c.company_name })), undo } });
      patchRows(list.map(c => c.company_number), { dismissed: !undo });
      setSelected({});
    } catch (e) { setError(e.message); }
  }

  async function exportRows(format) {
    setError("");
    try { await downloadPost("/api/sales/export", { format, companies: rows }, `prospects.${format}`); }
    catch (e) { setError(e.message); }
  }

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  const typeLabel = v => (meta?.company_types || []).find(t => t.value === v)?.label || v;

  function statusBadges(c) {
    return (
      <>
        {/* eslint-disable-next-line react/jsx-key */}
        {c.is_new && <Badge label="New" color="#30d158" bg="rgba(48,209,88,0.12)" />}
        {c.client_slug && <Badge label="Existing client" color="#30d158" bg="rgba(48,209,88,0.12)" title={c.client_slug} />}
        {c.lead_id && !c.client_slug && (
          <Badge label={`In pipeline · ${c.lead_stage || "New"}`} color={STAGE_META[c.lead_stage]?.color || "#00d4ff"} bg={STAGE_META[c.lead_stage]?.bg} />
        )}
        {c.dismissed && <Badge label="Dismissed" />}
      </>
    );
  }

  function rowActions(c) {
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
        {c.lead_id && c.lead_id > 0 ? (
          <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/sales/pipeline?lead=${c.lead_id}`); }}>Open lead</button>
        ) : !c.client_slug && (
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={e => { e.stopPropagation(); addToPipeline([c]); }}>Add</button>
        )}
        {!c.lead_id && !c.client_slug && (
          <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); dismiss([c], c.dismissed); }}>{c.dismissed ? "Undo" : "Dismiss"}</button>
        )}
        <a className="btn btn-secondary btn-sm" href={c.ch_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="View on Companies House">CH</a>
      </div>
    );
  }

  const gridCols = "28px minmax(0,2.2fr) 92px minmax(0,1.6fr) minmax(0,1.3fr) minmax(0,1.2fr) 190px";

  return (
    <div style={pageStyle(isMobile)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <h1 className="page-title" style={{ margin: "0 0 4px" }}>Prospect Search</h1>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>Newly incorporated companies from Companies House</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate("/sales/pipeline")}>Open Pipeline</button>
      </div>

      {meta && !meta.companies_house_configured && (
        <div style={{ marginBottom: 16 }}><ErrorBox>COMPANIES_HOUSE_KEY is not set, so searches will fail. Add it in System Admin.</ErrorBox></div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 280px", gap: 16, alignItems: "start" }}>
        {/* Search form */}
        <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 14, padding: isMobile ? 14 : 18, display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1.4fr) minmax(0,1fr)", gap: 14 }}>
            <div>
              <label style={labelStyle}>Locations, postcodes or districts</label>
              <input value={postcodes} onChange={e => setPostcodes(e.target.value)} onKeyDown={e => e.key === "Enter" && runSearch()}
                placeholder="e.g. Clackmannanshire, Stirling, RG1, SL4 1AA" style={inputStyle} />
              <div style={{ fontSize: 11, color: "#86868b", marginTop: 4 }}>Up to 10, separated by commas. Use a town, council area or county, a district like RG1, or a full postcode.</div>
            </div>
            <div>
              <label style={labelStyle}>Results per location</label>
              <select value={cap} onChange={e => setCap(Number(e.target.value))} style={inputStyle}>
                {[250, 500, 1000, 2000].map(n => <option key={n} value={n}>Up to {n.toLocaleString("en-GB")}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Incorporated in the last</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {RANGE_PRESETS.map(r => (
                <button className="btn btn-secondary btn-sm" aria-pressed={rangeMode === r.days} key={r.days} onClick={() => setRangeMode(r.days)}>{r.label}</button>
              ))}
              <button className="btn btn-secondary btn-sm" aria-pressed={rangeMode === "custom"} onClick={() => setRangeMode("custom")}>Custom</button>
            </div>
            {rangeMode === "custom" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <div><label style={labelStyle}>From</label><input type="date" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} style={inputStyle} /></div>
                <div><label style={labelStyle}>To</label><input type="date" value={dateTo} min={dateFrom} onChange={e => setDateTo(e.target.value)} style={inputStyle} /></div>
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Company types</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(meta?.company_types || []).map(t => (
                <button className="btn btn-secondary btn-sm" aria-pressed={types.includes(t.value)} key={t.value} onClick={() => toggle(types, setTypes, t.value)}>{t.label}</button>
              ))}
            </div>
            {types.length === 0 && <div style={{ fontSize: 11, color: "#ff9f0a", marginTop: 4 }}>No types selected: every company type will be included.</div>}
          </div>

          <div>
            <button className="btn btn-quiet btn-sm" onClick={() => setShowSectors(s => !s)}>
              <span style={{ ...labelStyle, margin: 0 }}>Sectors</span>
              <span style={{ fontSize: 11, color: "#86868b" }}>
                {includeSectors.length ? `${includeSectors.length} included` : "All"}{excludeSectors.length ? `, ${excludeSectors.length} excluded` : ""}
              </span>
              <i className={`ti ${showSectors ? "ti-chevron-up" : "ti-chevron-down"}`} style={{ color: "#86868b" }} />
            </button>
            {showSectors && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 11, color: "#86868b" }}>Click once to include only that sector, twice to exclude it, again to clear.</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(meta?.sectors || []).map(s => {
                    const inc = includeSectors.includes(s), exc = excludeSectors.includes(s);
                    return (
                      <button key={s} className="btn btn-secondary btn-sm" aria-pressed={inc || exc} style={{ color: exc ? "var(--red-ink)" : inc ? "var(--green-ink)" : undefined, textDecoration: exc ? "line-through" : "none" }}>{s}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 13, color: "#d1d1d6" }}>
              <input type="checkbox" checked={excludeDormant} onChange={e => setExcludeDormant(e.target.checked)} style={{ accentColor: "#00d4ff" }} />
              Hide dormant companies
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn btn-secondary" onClick={() => { setSaveName(postcodes.split(/[,;]+/).map(s => s.trim()).filter(Boolean).join(", ")); setShowSave(true); }} disabled={!postcodes.trim()}>Save search</button>
              <button className="btn btn-primary" style={{ opacity: searching ? 0.6 : 1 }} onClick={() => runSearch()} disabled={searching}>{searching ? "Searching…" : "Search"}</button>
            </div>
          </div>
        </div>

        {/* Saved searches */}
        <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 14, padding: 14, minWidth: 0 }}>
          <button className="btn btn-quiet btn-sm btn-block" style={{ justifyContent: "space-between" }} onClick={() => setShowSaved(s => !s)}>
            <span style={{ ...sectionTitle, margin: 0 }}>Saved searches {saved.length ? `(${saved.length})` : ""}</span>
            <i className={`ti ${showSaved ? "ti-chevron-up" : "ti-chevron-down"}`} style={{ color: "#86868b" }} />
          </button>
          {showSaved && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              {saved.length === 0 && <div style={{ fontSize: 12, color: "#86868b" }}>None yet. Run a search and choose Save search.</div>}
              {saved.map(s => (
                <div className="surface-dark" key={s.id} style={{ background: "#141414", border: "1px solid #2c2c2e", borderRadius: 10, padding: "9px 10px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <button className="btn btn-quiet btn-sm" style={{ textAlign: "left", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", flex: 1 }} onClick={() => runSaved(s)}>{s.name}</button>
                    {s.new_count > 0 && <Badge label={`${s.new_count} new`} color="#30d158" bg="rgba(48,209,88,0.12)" />}
                  </div>
                  <div style={{ fontSize: 11, color: "#86868b", marginTop: 3 }}>
                    Last {s.params?.days || 30} days · {s.last_total} results{s.last_run ? ` · Updated ${fmtUpdated(s.last_run)}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => runSaved(s)}>Run</button>
                    <button className="btn btn-secondary btn-sm" aria-pressed={s.schedule === "weekly"} onClick={() => savedAction(s, "toggle")} title="Refreshed weekly when this list is opened">
                      {s.schedule === "weekly" ? "Weekly on" : "Weekly off"}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => savedAction(s, "delete")}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent searches (v1.2) */}
      {history.length > 0 && (
        <div className="surface-dark" style={{ marginTop: 12, background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 14, padding: 14, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <button className="btn btn-quiet btn-sm" onClick={() => setShowHistory(v => !v)}>
              <span style={{ ...sectionTitle, margin: 0 }}>Recent searches ({history.length})</span>
              <i className={`ti ${showHistory ? "ti-chevron-up" : "ti-chevron-down"}`} style={{ color: "#86868b" }} />
            </button>
            {showHistory && <button className="btn btn-secondary btn-sm" onClick={() => historyAction(null, "clear")}>Clear</button>}
          </div>
          {showHistory && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))", gap: 8, marginTop: 10 }}>
              {history.map(h => {
                const d = describeParams(h.params);
                return (
                  <div className="surface-dark" key={h.id} style={{ background: "#141414", border: "1px solid #2c2c2e", borderRadius: 10, padding: "9px 10px", minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="btn btn-quiet btn-sm" style={{ textAlign: "left", overflow: "hidden", textOverflow: "ellipsis" }} onClick={() => historyAction(h, "fill")} title="Fill the form with this search"
                     >{d.where}</button>
                    <div style={{ fontSize: 11, color: "#86868b" }}>{d.detail} · {h.results} result{h.results === 1 ? "" : "s"} · {fmtUpdated(h.at)}</div>
                    <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                      <button className="btn btn-secondary btn-sm" disabled={searching} onClick={() => historyAction(h, "run")}>Run</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => historyAction(h, "fill")}>Edit</button>
                      <button className="btn btn-secondary btn-sm" style={{ marginLeft: "auto" }} title="Remove from recent searches" onClick={() => historyAction(h, "remove")}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <ErrorBox>{error}</ErrorBox>
        {notice && <div style={{ background: "rgba(48,209,88,0.08)", border: "1px solid rgba(48,209,88,0.3)", borderRadius: 8, padding: "10px 14px", color: "#30d158", fontSize: 13 }}>{notice}</div>}
      </div>

      {/* Results */}
      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                {rows.length.toLocaleString("en-GB")} shown <span style={{ color: "var(--text-muted)", fontWeight: 500, fontSize: 13 }}>of {result.companies.length.toLocaleString("en-GB")} matching</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                {fmtDate(result.params?.date_from)} to {fmtDate(result.params?.date_to)}
                {result.excluded_dormant ? ` · ${result.excluded_dormant} dormant hidden` : ""}
                {" · "}{(result.per_postcode || []).map(p => `${p.postcode} ${p.fetched}${p.truncated ? ` of ${p.total}` : ""}`).join(", ")}
              </div>
              {(result.per_postcode || []).some(p => p.truncated) && (
                <div style={{ fontSize: 12, color: "#ff9f0a", marginTop: 2 }}>Some locations have more results than the cap. Raise Results per location or narrow the dates.</div>
              )}
              {(result.warnings || []).length > 0 && <div style={{ fontSize: 12, color: "#ff9f0a", marginTop: 2 }}>{result.warnings.join("; ")}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn btn-secondary" onClick={() => exportRows("csv")} disabled={!rows.length}>Export CSV</button>
              <button className="btn btn-secondary" onClick={() => exportRows("xlsx")} disabled={!rows.length}>Export XLSX</button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
            <input value={textFilter} onChange={e => setTextFilter(e.target.value)} placeholder="Filter these results…" style={{ ...inputStyle, flex: "1 1 200px", minWidth: 0, width: "auto" }} />
            <button className="btn btn-secondary btn-sm" aria-pressed={hideKnown} onClick={() => setHideKnown(v => !v)}>Hide clients and pipeline</button>
            <button className="btn btn-secondary btn-sm" aria-pressed={hideDismissed} onClick={() => setHideDismissed(v => !v)}>Hide dismissed</button>
            {result.companies.some(c => c.is_new) && <button className="btn btn-secondary btn-sm" aria-pressed={onlyNew} onClick={() => setOnlyNew(v => !v)}>New only</button>}
          </div>

          {selectedRows.length > 0 && (
            <div style={{ position: "sticky", top: 8, zIndex: 5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#0b2a33", border: "1px solid #00d4ff55", borderRadius: 10, padding: "8px 12px", marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#f5f5f7", fontWeight: 600 }}>{selectedRows.length} selected</span>
              <button className="btn btn-primary" disabled={busy} onClick={() => addToPipeline(selectedRows)}>Add to pipeline</button>
              <button className="btn btn-secondary btn-sm" onClick={() => dismiss(selectedRows)}>Dismiss</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected({})}>Clear</button>
            </div>
          )}

          {rows.length === 0 ? (
            <div className="surface-dark" style={{ textAlign: "center", padding: "50px 20px", background: "#1c1c1e", borderRadius: 14, border: "1px solid #2c2c2e", color: "#86868b", fontSize: 13 }}>
              No companies match. Try a wider date range, more locations or fewer filters.
            </div>
          ) : isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rows.slice(0, 500).map(c => (
                <div className="surface-dark" key={c.company_number} style={{ background: "#1c1c1e", border: `1px solid ${selected[c.company_number] ? "#00d4ff" : "#2c2c2e"}`, borderRadius: 10, padding: "12px 12px", minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    {!c.lead_id && !c.client_slug && (
                      <input type="checkbox" checked={!!selected[c.company_number]} onChange={() => setSelected(s => ({ ...s, [c.company_number]: !s[c.company_number] }))} style={{ marginTop: 3, accentColor: "#00d4ff" }} />
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#f5f5f7", overflowWrap: "anywhere" }}>{c.company_name}</div>
                      <div style={{ fontSize: 12, color: "#86868b", marginTop: 2 }}>{c.company_number} · {fmtDate(c.incorporated)}</div>
                      <div style={{ fontSize: 12, color: "#86868b", marginTop: 2, overflowWrap: "anywhere" }}>{c.address}</div>
                      <div style={{ fontSize: 12, color: "#d1d1d6", marginTop: 4 }}>{(c.sectors || []).join(", ")}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>{statusBadges(c)}</div>
                      <div style={{ marginTop: 8 }}>{rowActions(c)}</div>
                    </div>
                  </div>
                </div>
              ))}
              {rows.length > 500 && <div style={{ fontSize: 12, color: "#86868b", textAlign: "center" }}>Showing the first 500. Filter the results or export to see them all.</div>}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 10, padding: "8px 14px", color: "#86868b", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", alignItems: "center" }}>
                <input type="checkbox" checked={allSelected} disabled={!selectable.length} onChange={() => {
                  if (allSelected) setSelected({});
                  else { const next = {}; selectable.forEach(c => { next[c.company_number] = true; }); setSelected(next); }
                }} style={{ accentColor: "#00d4ff" }} title="Select all that can be added" />
                <span>Company</span><span>Incorporated</span><span>Address</span><span>Sector</span><span>Status</span><span />
              </div>
              {rows.slice(0, 1000).map((c, i) => (
                <div key={c.company_number} style={{
                  display: "grid", gridTemplateColumns: gridCols, gap: 10, padding: "10px 14px", alignItems: "center",
                  background: selected[c.company_number] ? "rgba(0,212,255,0.06)" : (i % 2 === 0 ? "#161616" : "#1c1c1e"),
                  border: "1px solid #2c2c2e", borderRadius: 8, opacity: c.dismissed ? 0.55 : 1,
                }}>
                  {!c.lead_id && !c.client_slug ? (
                    <input type="checkbox" checked={!!selected[c.company_number]} onChange={() => setSelected(s => ({ ...s, [c.company_number]: !s[c.company_number] }))} style={{ accentColor: "#00d4ff" }} />
                  ) : <span />}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.company_name}>{c.company_name}</div>
                    <div style={{ fontSize: 11, color: "#86868b" }}>{c.company_number} · {typeLabel(c.company_type)}</div>
                  </div>
                  <span style={{ fontSize: 12, color: "#d1d1d6" }}>{fmtDate(c.incorporated)}</span>
                  <span style={{ fontSize: 12, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.address}>{c.address}</span>
                  <span style={{ fontSize: 12, color: "#d1d1d6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={(c.sic_codes || []).join(", ")}>{(c.sectors || []).join(", ")}</span>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", minWidth: 0 }}>{statusBadges(c)}</div>
                  {rowActions(c)}
                </div>
              ))}
              {rows.length > 1000 && <div style={{ fontSize: 12, color: "#86868b", textAlign: "center", padding: 10 }}>Showing the first 1,000. Filter the results or export to see them all.</div>}
            </div>
          )}
        </div>
      )}

      {showSave && (
        <Modal title="Save search" onClose={() => setShowSave(false)} footer={<>
          {/* eslint-disable-next-line react/jsx-key */}
          <button className="btn btn-secondary" onClick={() => setShowSave(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !saveName.trim()} onClick={saveSearch}>{busy ? "Saving…" : "Save"}</button>
        </>}>
          <div>
            <label style={labelStyle}>Name</label>
            <input value={saveName} onChange={e => setSaveName(e.target.value)} style={inputStyle} maxLength={80} autoFocus />
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 13, color: "#d1d1d6" }}>
            <input type="checkbox" checked={saveWeekly} onChange={e => setSaveWeekly(e.target.checked)} style={{ accentColor: "#00d4ff", marginTop: 2 }} />
            <span>Refresh weekly and count new companies. The date range rolls forward, so it always covers the most recent {rangeMode === "custom" ? "period of the same length" : `${rangeMode} days`}.</span>
          </label>
        </Modal>
      )}
    </div>
  );
}
